/* eslint-disable no-unused-vars */
import React, { useEffect, useReducer, useCallback, useMemo } from 'react';
import EngineScoringHeaderContainer from './EngineScoringHeaderContainer';
import EngineScoringTestContainer from './test/EngineScoringTestContainer';
import EngineScoringClassificationListComponent from './classifications/header/EngineScoringClassificationListComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import {
  ENGINE_SCORING_GRID_QUERY,
  SCORING_FILTER_TYPES_QUERY,
  SCORING_TARGET_CONTEXTS_QUERY,
  PERIOD_SELECTION_LIST_QUERY,
  SCORING_CATEGORIES_DICTIONARY_QUERY,
  DICTIONARY_APPLICATION_PAGE_QUERY,
  DICTIONARY_SCORING_RESPONSE_STATE_QUERY,
  DICTIONARY_KNOCKOUT_CRITERIA_QUERY,
  DICTIONARY_EXTERNAL_SYSTEM_INTERROGATION_QUERY,
  ENGINE_ATTRIBUTE_OPTIONS_QUERY,
  SCORING_FINANCIAL_INDICATOR_OPTIONS_QUERY,
  DICTIONARY_SCORING_RESPONSE_COLOR_QUERY
} from './queries';
import { PARTNER_BASE_TYPES_QUERY, PARTNER_TYPES_DICTIONARY_QUERY } from 'features/partnerTypes/components/tabs/queries';
import {
  ADD_UPDATE_ENGINE_SCORING_GRID_HEADER,
  ADD_UPDATE_ENGINE_CLASSIFICATION,
  ADD_UPDATE_ENGINE_SCORING_KNOCKOUT_CRITERION,
  ADD_UPDATE_ENGINE_INTERVALS,
  ADD_UPDATE_ENGINE_RULES,
  ADD_UPDATE_ENGINE_EXTERNAL_SYSTEMS,
  ADD_UPDATE_ENGINE_EVALUATED_ATTRIBUTES,
  DELETE_ENGINE_CLASSIFICATION,
  DELETE_ENGINE_KNOCKOUT_CRITERION
} from './mutations';
import {
  ENGINE_SCORING_GRID_HEADER_UPDATED,
  ENGINE_CLASSIFICATION_UPDATED,
  ENGINE_SCORING_KNOCKOUT_CRITERION_UPDATED,
  ENGINE_INTERVALS_UPDATED,
  ENGINE_RULES_UPDATED,
  ENGINE_EXTERNAL_SYSTEMS_UPDATED,
  ENGINE_EVALUATED_ATTRIBUTES_UPDATED,
  ENGINE_CLASSIFICATION_DELETED,
  ENGINE_KNOCKOUT_CRITERION_DELETED
} from './subscriptions';
import PropTypes from 'prop-types';
import { reducer, initialState } from '../../reducers/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import {
  engineScoringHeaderValidator,
  engineScoringClassificationsValidator,
  engineScoringIntervalsValidator,
  engineScoringRulesValidator,
  engineScoringKnockoutCriteriaValidator,
  engineScoringExternalSystemsValidator
} from 'features/engineScoringGrid/validators';
import EvaluatedAttributesContainer from './evaluatedAttributes/EvaluatedAttributeListContainer';
import EngineIntervalListContainer from './intervals/EngineIntervalListContainer';
import EngineRuleListContainer from './rules/EngineRuleListContainer';
import EngineScoringKnockoutCriteriaListContainer from './knockoutCriteria/EngineScoringKnockoutCriteriaListContainer';
import EngineExternalSystemListContainer from './externalSystems/EngineExternalSystemListContainer';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const EngineScoringContainer = ({
  engineScoringId,
  entityIdentifierForTest,
  onRedirect,
  onChangeTitle,
  openScoringTest,
  setCanTestScoring,
  onEntityIdentifierForTestChanged
}) => {
  const addToast = useToast();
  const [state, containerDispatcher] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  // #region Validations
  const [headerValidation, validateHeader] = useValidation(engineScoringHeaderValidator);
  const [classificationsValidation, validateClassifications] = useValidation(engineScoringClassificationsValidator);
  const [intervalsValidation, validateIntervals] = useValidation(engineScoringIntervalsValidator);
  const [rulesValidation, validateRules] = useValidation(engineScoringRulesValidator);
  const [knockoutCriteriaValidation, validateKnockoutCriteria] = useValidation(engineScoringKnockoutCriteriaValidator);
  const [externalSystemsValidation, validateExternalSystems] = useValidation(engineScoringExternalSystemsValidator);
  // #endregion

  // #region Queries
  const { data, loading } = useQuery(ENGINE_SCORING_GRID_QUERY, {
    variables: { engineScoringId },
    skip: engineScoringId === undefined || openScoringTest
  });

  const { data: filterTypesData } = useQuery(SCORING_FILTER_TYPES_QUERY, {
    skip: openScoringTest
  });

  const { data: targetContextsData } = useQuery(SCORING_TARGET_CONTEXTS_QUERY, {
    skip: openScoringTest
  });

  const { data: periodSelectionData } = useQuery(PERIOD_SELECTION_LIST_QUERY, {
    skip: openScoringTest
  });

  const { data: scoringCategoriesDictionaryData } = useQuery(SCORING_CATEGORIES_DICTIONARY_QUERY, {
    skip: openScoringTest
  });

  const { data: dictionaryApplicationPageData } = useQuery(DICTIONARY_APPLICATION_PAGE_QUERY, {
    skip: openScoringTest
  });

  const { data: dictionaryScoringResponseStateData } = useQuery(DICTIONARY_SCORING_RESPONSE_STATE_QUERY, {
    skip: openScoringTest
  });

  const { data: dictionaryKnockoutCriteriaData } = useQuery(DICTIONARY_KNOCKOUT_CRITERIA_QUERY, {
    skip: openScoringTest
  });

  const { data: dictionaryExternalSystemInterrogationData } = useQuery(DICTIONARY_EXTERNAL_SYSTEM_INTERROGATION_QUERY, {
    skip: openScoringTest
  });

  const { data: engineAttributeOptionsData } = useQuery(ENGINE_ATTRIBUTE_OPTIONS_QUERY, {
    skip: openScoringTest
  });

  const { data: scoringFinancialIndicatorOptionsData } = useQuery(SCORING_FINANCIAL_INDICATOR_OPTIONS_QUERY, {
    skip: openScoringTest
  });

  const { data: dictionaryScoringResponseColorData } = useQuery(DICTIONARY_SCORING_RESPONSE_COLOR_QUERY, {
    skip: openScoringTest
  });

  const { data: partnerBaseTypesData } = useQuery(PARTNER_BASE_TYPES_QUERY, {
    skip: openScoringTest
  });

  const { data: partnerTypesData } = useQuery(PARTNER_TYPES_DICTIONARY_QUERY);
  // #endregion

  // #region Mutations
  const [addOrUpdateEngineScoringGridHeader] = useMutation(ADD_UPDATE_ENGINE_SCORING_GRID_HEADER, {
    onError: () => {
      containerDispatcher({ type: 'OnHeaderSaved' });
    }
  });

  const [addOrUpdateEngineClassification] = useMutation(ADD_UPDATE_ENGINE_CLASSIFICATION);

  const [addOrUpdateEngineScoringKnockoutCriterion] = useMutation(ADD_UPDATE_ENGINE_SCORING_KNOCKOUT_CRITERION);

  const [addOrUpdateEngineIntervals] = useMutation(ADD_UPDATE_ENGINE_INTERVALS);

  const [addOrUpdateEngineRules] = useMutation(ADD_UPDATE_ENGINE_RULES);

  const [addOrUpdateEngineExternalSystems] = useMutation(ADD_UPDATE_ENGINE_EXTERNAL_SYSTEMS);

  const [addOrUpdateEngineEvaluatedAttributes] = useMutation(ADD_UPDATE_ENGINE_EVALUATED_ATTRIBUTES);

  const [deleteEngineScoringClassification] = useMutation(DELETE_ENGINE_CLASSIFICATION);

  const [deleteEngineScoringKnockoutCriterion] = useMutation(DELETE_ENGINE_KNOCKOUT_CRITERION);
  // #endregion

  // #region Subscriptions
  useSubscription(ENGINE_SCORING_GRID_HEADER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      containerDispatcher({ type: 'OnHeaderSaved' });
      addToast(t('EngineScoringGrid.EngineScoringHeaderUpdated'), 'success');
      if (engineScoringId === undefined) onRedirect(data.engineScoringGridHeaderUpdated.engineScoring.engineScoringId);
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineScoringGridHeaderUpdated.engineScoring.engineScoringId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedEngineScoringGrids' });
      client.cache.gc();
    }
  });

  useSubscription(ENGINE_CLASSIFICATION_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringClassificationUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineClassificationUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_SCORING_KNOCKOUT_CRITERION_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringKnockoutCriterionUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineScoringKnockoutCriterionUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_INTERVALS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineIntervalsUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineIntervalsUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_RULES_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineRulesUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineRulesUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_EXTERNAL_SYSTEMS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringXExternalSystemsUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineExternalSystemsUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_EVALUATED_ATTRIBUTES_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringXAttributesUpdated'), 'success');
      client.cache.evict({
        id: `EngineScoringGridItem:{"engineScoringId":${data.engineEvaluatedAttributesUpdated.engineScoringId}}`
      });
    }
  });

  useSubscription(ENGINE_CLASSIFICATION_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringClassificationRemoved'), 'success');
      client.cache.evict({
        id: `EngineScoringGridClassificationItem:{"engineScoringClassificationId":${data.engineScoringClassificationDeleted.engineScoringClassificationId}}`
      });
    }
  });

  useSubscription(ENGINE_KNOCKOUT_CRITERION_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringKnockoutCriterionRemoved'), 'success');
      client.cache.evict({
        id: `EngineScoringGridKnockoutCriteriaItem:{"engineScoringKnockoutCriteriaId":${data.engineScoringKnockoutCriterionDeleted.engineScoringKnockoutCriteriaId}}`
      });
    }
  });
  // #endregion

  // #region Use effects
  const loaded = useMemo(() => !loading && data, [data, loading]);
  const isReadOnly = useMemo(() => (data && data.engineScoringGrid ? data.engineScoringGrid.isReadOnly : false), [data]);

  useEffect(() => {
    if (loaded) {
      if (state.header.entityIdentifierForTest && state.header.targetContextId) setCanTestScoring(true);
      else setCanTestScoring(false);
    }
  }, [loaded, setCanTestScoring, state.header]);

  useEffect(() => {
    if (!openScoringTest && state.header.entityIdentifierForTest) onEntityIdentifierForTestChanged(state.header.entityIdentifierForTest);
  }, [onEntityIdentifierForTestChanged, openScoringTest, state.header.entityIdentifierForTest]);

  useEffect(() => {
    if (!isReadOnly && (loaded || (!loaded && !engineScoringId))) validateHeader(state.header);
  }, [engineScoringId, isReadOnly, loaded, state.header, validateHeader]);

  useEffect(() => {
    if (!isReadOnly && loaded) validateClassifications(state.classifications);
  }, [isReadOnly, loaded, state.classifications, validateClassifications]);

  useEffect(() => {
    if (!isReadOnly && loaded) validateIntervals(state.intervals.engineIntervals);
  }, [isReadOnly, loaded, state.intervals.engineIntervals, validateIntervals]);

  useEffect(() => {
    if (!isReadOnly && loaded) validateRules(state.rules.engineRules);
  }, [isReadOnly, loaded, state.rules.engineRules, validateRules]);

  useEffect(() => {
    if (!isReadOnly && loaded) validateKnockoutCriteria(state.knockoutCriteria);
  }, [isReadOnly, loaded, state.knockoutCriteria, validateKnockoutCriteria]);

  useEffect(() => {
    if (!isReadOnly && loaded) validateExternalSystems(state.externalSystems.engineExternalSystems);
  }, [isReadOnly, loaded, state.externalSystems.engineExternalSystems, validateExternalSystems]);

  useEffect(() => {
    if (loaded && state.header.name) {
      onChangeTitle(state.header.name);
    }
  }, [loaded, onChangeTitle, state.header.name]);

  useEffect(() => {
    if (!engineScoringId) containerDispatcher({ type: 'OnAddEngineScoring' });
    else if (data && data.engineScoringGrid) containerDispatcher({ type: 'OnInitState', engineScoring: data.engineScoringGrid });
  }, [data, engineScoringId]);

  // #endregion

  const onSave = useCallback(
    (type, data, index) => {
      switch (type) {
        case 'OnSaveHeader': {
          if (!validateHeader(state.header)) return;

          containerDispatcher({ type: 'BeforeSavingHeader' });
          const {
            changed,
            saving,
            isReadOnly,
            initialCollectionPartnerBaseTypes,
            initialCollectionPartnerTypes,
            partnerBaseTypeIds,
            partnerTypeId,
            partnerTypeIds,
            ...engineScoringGridHeader
          } = state.header;

          let deletedItems = {
            partnerTypes: [...state.deletedItems.partnerTypes],
            partnerBaseTypes: [...state.deletedItems.partnerBaseTypes]
          };

          return addOrUpdateEngineScoringGridHeader({
            variables: { input: { engineScoringGridHeader, deletedItems } }
          });
        }
        case 'OnSaveClassification': {
          const { scoringClassification, classificationIndex } = data;
          const { changed, isExpanded, saving, ...engineScoringClassification } = scoringClassification;
          containerDispatcher({ type: 'BeforeSavingClassification', classificationIndex });
          let deletedItems = {
            attributes: [...state.deletedItems.attributes],
            categories: [...state.deletedItems.categories],
            financialIndicators: [...state.deletedItems.financialIndicators]
          };

          const categories = engineScoringClassification.categories.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { scoringName, ...category } = x;
            return category;
          });

          const attributes = engineScoringClassification.attributes.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { description, ...attribute } = x;
            return attribute;
          });

          const financialIndicators = engineScoringClassification.financialIndicators.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { financialIndicatorName, ...indicator } = x;
            return indicator;
          });

          const input = {
            engineScoringId,
            engineScoringClassification: {
              ...engineScoringClassification,
              categories,
              attributes,
              financialIndicators
            },
            deletedItems
          };

          addOrUpdateEngineClassification({
            variables: { input }
          });

          return containerDispatcher({
            type: 'OnClassificationSaved',
            classificationIndex: classificationIndex
          });
        }

        case 'OnSaveKnockoutCriterion': {
          const { criterion, index } = data;
          const { changed, isExpanded, saving, ...engineScoringKnockoutCriterion } = criterion;
          containerDispatcher({ type: 'BeforeSavingKnockoutCriterion', index });

          addOrUpdateEngineScoringKnockoutCriterion({
            variables: { input: { engineScoringKnockoutCriterion } }
          });

          return containerDispatcher({ type: 'OnKnockoutCriterionSaved', index });
        }

        case 'OnSaveEvaluatedAttributes': {
          containerDispatcher({ type: 'BeforeSavingEvaluatedAttributes' });

          addOrUpdateEngineEvaluatedAttributes({
            variables: {
              input: {
                engineScoringId,
                useAllAttributes: state.evaluatedAttributes.useAllAttributes,
                evaluatedAttributes: state.evaluatedAttributes.allAttributes,
                deletedItems: state.deletedItems.evaluatedAttributes
              }
            }
          });

          return containerDispatcher({ type: 'OnEvaluatedAttributesSaved' });
        }
        case 'OnSaveEngineIntervals': {
          if (!validateIntervals(state.intervals.engineIntervals)) return;

          containerDispatcher({ type: 'BeforeSavingEngineIntervals' });

          addOrUpdateEngineIntervals({
            variables: {
              input: {
                engineScoringId,
                engineIntervals: state.intervals.engineIntervals,
                deletedItems: state.deletedItems.intervals
              }
            }
          });

          return containerDispatcher({ type: 'OnEngineScoringIntervalsSaved' });
        }
        case 'OnSaveEngineRules': {
          if (!validateRules(state.rules.engineRules)) return;

          containerDispatcher({ type: 'BeforeSavingEngineRules' });

          addOrUpdateEngineRules({
            variables: {
              input: { engineScoringId, engineRules: state.rules.engineRules, deletedItems: state.deletedItems.rules }
            }
          });

          return containerDispatcher({ type: 'OnEngineScoringRulesSaved' });
        }
        case 'OnSaveEngineExternalSystems': {
          if (!validateExternalSystems(state.externalSystems.engineExternalSystems)) return;

          containerDispatcher({ type: 'BeforeSavingEngineExternalSystems' });

          addOrUpdateEngineExternalSystems({
            variables: {
              input: {
                engineScoringId,
                externalSystems: state.externalSystems.engineExternalSystems,
                deletedItems: state.deletedItems.externalSystems
              }
            }
          });

          return containerDispatcher({ type: 'OnEngineExternalSystemsSaved' });
        }
        case 'OnDeleteClassification': {
          const { changed, saving, ...engineScoringClassification } = data;

          if (!engineScoringClassification.engineScoringClassificationId) {
            containerDispatcher({ type: 'OnClassificationDeleted', engineClassificationIndex: index });
            return;
          }

          const categories = engineScoringClassification.categories.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { scoringName, ...category } = x;
            return category;
          });

          const attributes = engineScoringClassification.attributes.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { description, ...attribute } = x;
            return attribute;
          });

          const financialIndicators = engineScoringClassification.financialIndicators.map(x => {
            // eslint-disable-next-line no-unused-vars
            const { financialIndicatorName, ...indicator } = x;
            return indicator;
          });

          const input = {
            engineScoringId,
            engineScoringClassification: { ...engineScoringClassification, categories, attributes, financialIndicators }
          };

          return deleteEngineScoringClassification({
            variables: { input }
          });
        }
        case 'OnDeleteEngineScoringKnockoutCriteria': {
          if (!data.engineScoringKnockoutCriteriaId) {
            containerDispatcher({ type: 'OnKnockoutCriterionDeleted', index });
            return;
          }

          return deleteEngineScoringKnockoutCriterion({
            variables: { input: { engineScoringKnockoutCriteriaId: data.engineScoringKnockoutCriteriaId } }
          });
        }

        default:
          return;
      }
    },
    [
      addOrUpdateEngineClassification,
      addOrUpdateEngineEvaluatedAttributes,
      addOrUpdateEngineExternalSystems,
      addOrUpdateEngineIntervals,
      addOrUpdateEngineRules,
      addOrUpdateEngineScoringGridHeader,
      addOrUpdateEngineScoringKnockoutCriterion,
      deleteEngineScoringClassification,
      deleteEngineScoringKnockoutCriterion,
      engineScoringId,
      state.deletedItems.attributes,
      state.deletedItems.categories,
      state.deletedItems.evaluatedAttributes,
      state.deletedItems.externalSystems,
      state.deletedItems.financialIndicators,
      state.deletedItems.intervals,
      state.deletedItems.partnerBaseTypes,
      state.deletedItems.partnerTypes,
      state.deletedItems.rules,
      state.evaluatedAttributes.allAttributes,
      state.evaluatedAttributes.useAllAttributes,
      state.externalSystems.engineExternalSystems,
      state.header,
      state.intervals.engineIntervals,
      state.rules.engineRules,
      validateExternalSystems,
      validateHeader,
      validateIntervals,
      validateRules
    ]
  );

  return (
    <>
      {openScoringTest ? (
        <div>
          <EngineScoringTestContainer engineScoringId={engineScoringId} entityIdentifierForTest={entityIdentifierForTest} />
        </div>
      ) : (
        <div>
          <EngineScoringHeaderContainer
            containerDispatcher={containerDispatcher}
            engineScoringId={engineScoringId}
            header={state.header}
            isReadOnly={isReadOnly}
            loading={loading || state === initialState}
            onChangeTitle={onChangeTitle}
            onSave={onSave}
            validation={headerValidation}
            canTestScoring={Boolean(state.header.entityIdentifierForTest && state.header.targetContextId)}
            filterTypes={filterTypesData ? filterTypesData.scoringFilterTypes : []}
            targetContexts={targetContextsData ? targetContextsData.scoringTargetContexts : []}
            periodSelectionList={periodSelectionData ? periodSelectionData.periodSelectionList : []}
            partnerBaseTypes={partnerBaseTypesData ? partnerBaseTypesData.baseTypes : []}
            partnerTypes={partnerTypesData ? partnerTypesData.partnerTypesDictionary : []}
          />
          {engineScoringId && (
            <>
              <EvaluatedAttributesContainer
                containerDispatcher={containerDispatcher}
                evaluatedAttributes={state.evaluatedAttributes}
                isReadOnly={isReadOnly}
                loading={loading}
                onSave={onSave}
                engineAttributeOptions={engineAttributeOptionsData ? engineAttributeOptionsData.engineAttributeOptions : []}
              />
              <EngineIntervalListContainer
                containerDispatcher={containerDispatcher}
                intervals={state.intervals}
                isReadOnly={isReadOnly}
                loading={loading}
                onSave={onSave}
                validation={intervalsValidation}
                dictionaryScoringResponseColor={
                  dictionaryScoringResponseColorData ? dictionaryScoringResponseColorData.dictionaryScoringResponseColor : []
                }
              />
              <EngineRuleListContainer
                containerDispatcher={containerDispatcher}
                rules={state.rules}
                isReadOnly={isReadOnly}
                loading={loading}
                onSave={onSave}
                validation={rulesValidation}
              />
              <EngineScoringClassificationListComponent
                classifications={state.classifications}
                engineScoringId={engineScoringId}
                isReadOnly={isReadOnly}
                dispatch={containerDispatcher}
                loading={loading}
                onSave={onSave}
                validation={classificationsValidation}
                scoringCategoryOptions={scoringCategoriesDictionaryData ? scoringCategoriesDictionaryData.scoringCategoriesDictionary : []}
                dictionaryApplicationPage={dictionaryApplicationPageData ? dictionaryApplicationPageData.dictionaryApplicationPage : []}
                dictionaryScoringResponseState={
                  dictionaryScoringResponseStateData ? dictionaryScoringResponseStateData.dictionaryScoringResponseState : []
                }
                engineAttributeOptions={engineAttributeOptionsData ? engineAttributeOptionsData.engineAttributeOptions : []}
                scoringFinancialIndicatorOptions={
                  scoringFinancialIndicatorOptionsData ? scoringFinancialIndicatorOptionsData.scoringFinancialIndicatorOptions : []
                }
                useAllAttributes={data && data.engineScoringGrid ? data.engineScoringGrid.useAllAttributes : false}
                evaluatedAttributes={
                  data && data.engineScoringGrid
                    ? data.engineScoringGrid.evaluatedAttributes.map(x => {
                        return { ...x.engineAttribute };
                      })
                    : []
                }
              />
              <EngineScoringKnockoutCriteriaListContainer
                knockoutCriteria={state.knockoutCriteria}
                onSave={onSave}
                containerDispatcher={containerDispatcher}
                isReadOnly={data && data.engineScoringGrid ? data.engineScoringGrid.isReadOnly : true}
                validation={knockoutCriteriaValidation}
                loading={loading}
                dictionaryKnockoutCriteria={dictionaryKnockoutCriteriaData ? dictionaryKnockoutCriteriaData.dictionaryKnockoutCriteria : []}
              />
              <EngineExternalSystemListContainer
                externalSystems={state.externalSystems}
                onSave={onSave}
                containerDispatcher={containerDispatcher}
                isReadOnly={data && data.engineScoringGrid ? data.engineScoringGrid.isReadOnly : true}
                validation={externalSystemsValidation}
                loading={loading}
                dictionaryExternalSystemInterrogation={
                  dictionaryExternalSystemInterrogationData
                    ? dictionaryExternalSystemInterrogationData.dictionaryExternalSystemInterrogation
                    : []
                }
              />
            </>
          )}
        </div>
      )}
      <Prompt
        when={
          (state.header.changed && !state.header.saving) ||
          state.classifications.some(x => x.changed) ||
          state.intervals.changed ||
          state.rules.changed ||
          state.knockoutCriteria.some(x => x.changed) ||
          state.externalSystems.changed ||
          state.evaluatedAttributes.changed
        }
        message={t('EngineScoring.LeavingWithoutSavingEngineScoring')}
      />
    </>
  );
};

EngineScoringContainer.propTypes = {
  engineScoringId: PropTypes.number,
  onRedirect: PropTypes.func.isRequired,
  onChangeTitle: PropTypes.func.isRequired,
  openScoringTest: PropTypes.bool.isRequired,
  setCanTestScoring: PropTypes.func.isRequired,
  onEntityIdentifierForTestChanged: PropTypes.func,
  entityIdentifierForTest: PropTypes.string
};

export default EngineScoringContainer;
